import type { inferRouterInputs, inferRouterOutputs } from '@sweetistics/trpc-server';
import type { AppRouter } from '@/types/app-router';
import { createTrpcClient as createTrpcClientImpl, createTrpcQueryClient as createTrpcQueryClientImpl } from './base';
import trpcInstance from './react';
import { createTrpcSegment, type TrpcSegment } from './type-helpers';

const trpcProxy: typeof trpcInstance = trpcInstance;
type TrpcReactClient = typeof trpcInstance;
export const trpc = trpcProxy;
export const getTrpcRouter = () => trpcProxy;

export function createTrpcClient(...parameters: Parameters<typeof createTrpcClientImpl>) {
  return createTrpcClientImpl(...parameters);
}

export function createTrpcQueryClient(...parameters: Parameters<typeof createTrpcQueryClientImpl>) {
  return createTrpcQueryClientImpl(...parameters);
}

const resolveSegment = <T>(segment: T | undefined, label: string): TrpcSegment<T> =>
  createTrpcSegment(segment, { label });

export const trpcSystem: TrpcSegment<typeof trpcProxy.system> = createTrpcSegment(trpcProxy.system);
export const trpcExplore = createTrpcSegment(trpcProxy.explore);
export const trpcTimelineAnalysis = createTrpcSegment(trpcProxy.timelineAnalysis);
export const trpcTimeline = createTrpcSegment(trpcProxy.timeline);
export const trpcTweets = createTrpcSegment(trpcProxy.tweets);
export const trpcArchive = createTrpcSegment(trpcProxy.archive);
export const trpcAccounts = createTrpcSegment(trpcProxy.accounts);
export const trpcUtilities = createTrpcSegment(trpcProxy.utilities);
export const trpcInteractions = createTrpcSegment(trpcProxy.interactions);
export const trpcActivities = createTrpcSegment(trpcProxy.activities);
export const trpcStreamHandlers = createTrpcSegment(trpcProxy.streamHandlersRouter);
export const trpcDebug = createTrpcSegment(trpcProxy.debug);
export const trpcDms = createTrpcSegment(trpcProxy.dms);
export const trpcProfiles = createTrpcSegment(trpcProxy.profiles);
export const trpcPulse = createTrpcSegment(trpcProxy.pulse);
export const trpcArena = createTrpcSegment(trpcProxy.arena);
export const trpcAuth = createTrpcSegment(trpcProxy.auth);
export const trpcExtension = createTrpcSegment(trpcProxy.extension);
export const trpcJobs = createTrpcSegment(trpcProxy.jobs);
export const trpcSettings = createTrpcSegment(trpcProxy.settings);
export const trpcUserAnalysis = createTrpcSegment(trpcProxy.userAnalysis);
export const trpcUserFeatures = createTrpcSegment(trpcProxy.userFeatures);
export const trpcSync = createTrpcSegment(trpcProxy.sync);
export const trpcUser = createTrpcSegment(trpcProxy.user);
export const trpcSocial = createTrpcSegment(trpcProxy.social);
export const trpcSocialStats = createTrpcSegment(trpcProxy.socialStats);
export const trpcSearch = createTrpcSegment(trpcProxy.search);

const trpcAdminSegment = resolveSegment(trpcProxy.admin, 'admin');
export const trpcAdmin = createTrpcSegment<TrpcReactClient['admin']>(trpcAdminSegment);
export const trpcAdminApiKeys = createTrpcSegment<TrpcReactClient['admin']['apiKeys']>(
  resolveSegment(trpcAdminSegment.apiKeys, 'admin.apiKeys')
);
export const trpcAdminApify = createTrpcSegment<TrpcReactClient['admin']['apify']>(
  resolveSegment(trpcAdminSegment.apify, 'admin.apify')
);
export const trpcAdminAi = createTrpcSegment<TrpcReactClient['admin']['ai']>(
  resolveSegment(trpcAdminSegment.ai, 'admin.ai')
);
export const trpcAdminAiConfig = createTrpcSegment<TrpcReactClient['admin']['aiConfig']>(
  resolveSegment(trpcAdminSegment.aiConfig, 'admin.aiConfig')
);
export const trpcAdminAiDebug = createTrpcSegment<TrpcReactClient['admin']['aiDebug']>(
  resolveSegment(trpcAdminSegment.aiDebug, 'admin.aiDebug')
);
export const trpcAdminDatabase = createTrpcSegment<TrpcReactClient['admin']['database']>(
  resolveSegment(trpcAdminSegment.database, 'admin.database')
);
export const trpcAdminDebug = createTrpcSegment<TrpcReactClient['admin']['debug']>(
  resolveSegment(trpcAdminSegment.debug, 'admin.debug')
);
export const trpcAdminDigests = createTrpcSegment<TrpcReactClient['admin']['digests']>(
  resolveSegment(trpcAdminSegment.digests, 'admin.digests')
);
export const trpcAdminFollows = createTrpcSegment<TrpcReactClient['admin']['follows']>(
  resolveSegment(trpcAdminSegment.follows, 'admin.follows')
);
export const trpcAdminInngest = createTrpcSegment<TrpcReactClient['admin']['inngest']>(
  resolveSegment(trpcAdminSegment.inngest, 'admin.inngest')
);
export const trpcAdminLogLevel = createTrpcSegment<TrpcReactClient['admin']['logLevel']>(
  resolveSegment(trpcAdminSegment.logLevel, 'admin.logLevel')
);
export const trpcAdminLogs = createTrpcSegment<TrpcReactClient['admin']['logs']>(
  resolveSegment(trpcAdminSegment.logs, 'admin.logs')
);
export const trpcAdminOpenCage = createTrpcSegment<TrpcReactClient['admin']['opencage']>(
  resolveSegment(trpcAdminSegment.opencage, 'admin.opencage')
);
export const trpcAdminQueue = createTrpcSegment<TrpcReactClient['admin']['queue']>(
  resolveSegment(trpcAdminSegment.queue, 'admin.queue')
);
export const trpcAdminSettings = createTrpcSegment<TrpcReactClient['admin']['settings']>(
  resolveSegment(trpcAdminSegment.settings, 'admin.settings')
);
export const trpcAdminStripe = createTrpcSegment<TrpcReactClient['admin']['stripe']>(
  resolveSegment(trpcAdminSegment.stripe, 'admin.stripe')
);
export const trpcAdminSystem = createTrpcSegment<TrpcReactClient['admin']['systemAdmin']>(
  resolveSegment(trpcAdminSegment.systemAdmin, 'admin.systemAdmin')
);
export const trpcAdminTurbopuffer = createTrpcSegment<TrpcReactClient['admin']['turbopuffer']>(
  resolveSegment(trpcAdminSegment.turbopuffer, 'admin.turbopuffer')
);
export const trpcAdminTweets = createTrpcSegment<TrpcReactClient['admin']['tweetsAdmin']>(
  resolveSegment(trpcAdminSegment.tweetsAdmin, 'admin.tweetsAdmin')
);
export const trpcAdminTwitter = createTrpcSegment<TrpcReactClient['admin']['twitter']>(
  resolveSegment(trpcAdminSegment.twitter, 'admin.twitter')
);
export const trpcAdminUsers = createTrpcSegment<TrpcReactClient['admin']['users']>(
  resolveSegment(trpcAdminSegment.users, 'admin.users')
);
export const trpcAdminXActivity = createTrpcSegment<TrpcReactClient['admin']['xActivity']>(
  resolveSegment(trpcAdminSegment.xActivity, 'admin.xActivity')
);

export type TrpcAdminRouter = typeof trpcAdmin;
export type TrpcAdminUsersRouter = typeof trpcAdminUsers;
type EnsureEmbeddabilityInput = inferRouterInputs<AppRouter>['utilities']['ensureEmbeddability'];
type EnsureEmbeddabilityOutput = inferRouterOutputs<AppRouter>['utilities']['ensureEmbeddability'];
type LinkPreviewContextInput = inferRouterInputs<AppRouter>['utilities']['linkPreviewContext'];
type LinkPreviewContextOutput = inferRouterOutputs<AppRouter>['utilities']['linkPreviewContext'];

type EnsureEmbeddabilityMock = (input: EnsureEmbeddabilityInput) => Promise<EnsureEmbeddabilityOutput>;
type LinkPreviewContextMock = (input: LinkPreviewContextInput) => Promise<LinkPreviewContextOutput>;

let ensureEmbeddabilityMock: EnsureEmbeddabilityMock | null = null;
let linkPreviewContextMock: LinkPreviewContextMock | null = null;

export function __setEnsureEmbeddabilityMock(mock: EnsureEmbeddabilityMock) {
  ensureEmbeddabilityMock = mock;
}

export function __resetEnsureEmbeddabilityMock() {
  ensureEmbeddabilityMock = null;
}

export function __getEnsureEmbeddabilityMock(): EnsureEmbeddabilityMock | null {
  return ensureEmbeddabilityMock;
}

export function __setLinkPreviewContextMock(mock: LinkPreviewContextMock) {
  linkPreviewContextMock = mock;
}

export function __resetLinkPreviewContextMock() {
  linkPreviewContextMock = null;
}

export function __getLinkPreviewContextMock(): LinkPreviewContextMock | null {
  return linkPreviewContextMock;
}
